<template>
    <modal v-bind:show.sync="show" ref="modal">
        <div slot="header">
            <h3 class="panel-title f-s-5">{{ popup.encoded_id ? trans('Edit Sales Popup') : trans('Add Sales Popup') }}</h3>
        </div>

        <!-- Name -->
        <div v-bind:class="{'has-error': errors.has('name')}" class="form-group">
            <label>
                {{ trans('Sales Popup Name (internal)') }}
                <input class="input__field" type="text" v-model="popup.name" v-bind:placeholder="trans('e.g. Course promo popup')">
            </label>
        </div>
        <div v-if="errors.has('name')" class="form-help">{{ errors.get('name') }}</div>

        <!-- Headline -->
        <div v-bind:class="{'has-error': errors.has('content.headline')}" class="form-group form-group-simple">
            <label class="m-b-2">{{ trans('Headline') }}</label>
            <rich-text-editor
                id="sales_popup_headline"
                name="sales_popup_headline"
                height="100px"
                v-bind:key="'headline-' + index"
                v-bind:content="popup.content.headline"
                v-bind:buttons="['bold', 'italic', 'underline']"
                v-bind:plugins="[]"
                v-bind:variables="mergeVars"
                v-bind:paste-plain-text="true"
                v-on:input="popup.content.headline = $event"
            ></rich-text-editor>
        </div>
        <div v-if="errors.has('content.headline')" class="form-help">{{ errors.get('content.headline') }}</div>

        <!-- Body -->
        <div class="form-group form-group-simple">
            <label class="m-b-2">{{ trans('Body') }}</label>
            <rich-text-editor
                id="sales_popup_body"
                name="sales_popup_body"
                v-bind:key="'body-' + index"
                v-bind:content="popup.content.body"
                v-bind:variables="mergeVars"
                v-on:input="popup.content.body = $event"
            ></rich-text-editor>
        </div>

        <!-- Media -->
        <div class="form-group">
            <label class="m-b-2">{{ trans('Media Type') }}</label>
            <select class="input__field" v-model="popup.media_type">
                <option value="image">{{ trans('Image') }}</option>
                <option value="video">{{ trans('Video') }}</option>
            </select>
        </div>

        <div v-if="popup.media_type === 'image'" class="form-group form-group-simple">
            <label class="m-b-2">{{ trans('Image') }}</label>
            <image-upload
                field-name="sales_popup_image"
                v-bind:existing-file-url="popup.image_url ? popup.image_url.full : null"
                v-bind:existing-file-path="popup.image"
                v-bind:max-filesize="2"
                v-on:image-selected="addImage"
                v-on:image-removed="removeImage"
            ></image-upload>
            <div v-if=" ! popup.image" class="c-muted f-s-1 m-t-1">{{ trans('Make this image JPEG, PNG, GIF, BMP or WEBP; 800-1920px wide.') }}</div>
        </div>

        <div v-else-if="popup.media_type === 'video'" class="form-group form-group-simple">
            <label class="m-b-2">{{ trans('Video') }}</label>
            <video-with-thumbnail-upload
                field-name="sales_popup_video"
                v-bind:existing-file-url="popup.video_url"
                v-bind:existing-video-type="popup.video_type"
                v-bind:existing-video-image="originalVideoThumbnailUrl"
                v-bind:existing-video-embed-code="popup.video_embed_code"
                v-bind:allow-upload="true"
                video-hosting-provider="bunny"
                v-on:video-removed="removeVideo"
                v-on:video-processed="addVideo"
                v-on:thumbnail-selected="addVideoThumbnail"
            ></video-with-thumbnail-upload>
            <div v-if=" ! popup.image" class="c-muted f-s-1 m-t-1">{{ trans('Make this video 800-1920px wide and smaller than 1GB file size.') }}</div>
        </div>

        <!-- Price -->
        <div v-if="showPrice" class="form-group">
            <label>{{ trans('Price') }}</label>
            <select class="input__field" v-model="popup.content.price">
                <option value="hide">{{ trans("Don't show price") }}</option>
                <option value="first">{{ trans('First available plan') }}</option>
                <option v-for="plan in pricingPlans" v-bind:key="plan.encoded_id" v-bind:value="plan.encoded_id">{{ plan.name }}</option>
            </select>
        </div>

        <!-- Button Label -->
        <div v-bind:class="{'has-error': errors.has('content.buttonLabel')}" class="form-group">
            <input-with-vars
                class="input__field"
                name="button_label"
                v-bind:label="trans('Button Label')"
                v-bind:variables="mergeVars"
                v-model="popup.content.buttonLabel"
                v-on:update:value="popup.content.buttonLabel = $event"
            ></input-with-vars>
        </div>
        <div v-if="errors.has('content.buttonLabel')" class="form-help">{{ errors.get('content.buttonLabel') }}</div>

        <!-- Button Color -->
        <color-picker
            field-name-prefix="button_color"
            v-bind:color-label="trans('Button Color')"
            v-bind:use-opacity="false"
            v-bind:value="{color: popup.content.buttonColor}"
            v-bind:has-error="errors.has('content.buttonColor')"
            v-on:update:color="popup.content.buttonColor = $event"
        ></color-picker>
        <div v-if="errors.has('content.buttonColor')" class="form-help">{{ errors.get('content.buttonColor') }}</div>

        <!-- Button URL -->
        <div v-bind:class="{'has-error': errors.has('content.buttonUrl')}" class="form-group">
            <label>
                {{ trans('Button URL') }}
                <input class="input__field" type="text" v-model="popup.content.buttonUrl" placeholder="https://">
            </label>
        </div>
        <div class="form-help">{{ trans('If left empty, the member will be taken to the membership checkout page.') }}</div>
        <div v-if="errors.has('content.buttonUrl')" class="form-help">{{ errors.get('content.buttonUrl') }}</div>

        <div slot="footer" class="p-b-6">
            <div class="row row-collapse">
                <div class="column">
                    <button type="button" class="btn btn-primary m-r-3" v-on:click="savePopup">{{ trans('Save') }}</button>
                </div>
                <div v-if="popup.encoded_id" class="text-right">
                    <button type="button" class="btn btn-outline btn-outline--alert" v-on:click="deletePopup">{{ trans('Delete') }}</button>
                </div>
            </div>
        </div>
    </modal>
</template>
<script>
import Modal from '../modals/Modal.vue';
import ColorPicker from '../fields/ColorPicker.vue';
import Errors from '../../errors.js';
import { scrollToError } from '../../helpers.js';

const salesPopupValidationUrl = (membershipEncodedId) => {
    return window.Kourses.apiBaseUrl + '/memberships/' + membershipEncodedId + '/sales-popups/validate';
};

export default {
    components: {
        Modal,
        ColorPicker,
    },
    props: {
        pricingPlans: {
            type: Array,
            required: true,
        },
        membership: {
            type: Object,
            required: true,
        },
        defaultButtonColor: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            show: false,
            popup: this.getEmptyPopup(),
            errors: new Errors(),
            index: null,
            mergeVars: {
                producttitle: this.trans('Product title'),
            },
            originalVideoThumbnailUrl: null,
        };
    },
    computed: {
        showPrice() {
            return this.pricingPlans.length > 0 && ! this.pricingPlans.some(plan => plan.type === 'free');
        },
    },
    watch: {
        show(newValue) {
            if (newValue === false) {
                this.popup = this.getEmptyPopup();
                this.index = null;
                this.errors.clear();
                this.originalVideoThumbnailUrl = null;
            }
        },
    },
    methods: {
        savePopup() {
            this.errors.clear();

            return window.axios.post(salesPopupValidationUrl(this.membership.encoded_id), this.popup)
                .then(() => {
                    this.$emit('save', this.popup, this.index);
                    this.show = false;
                })
                .catch((error) => {
                    this.errors.record(error.response.data);

                    this.$nextTick(() => {
                        scrollToError(this.$el);
                    });
                });
        },
        deletePopup() {
            this.$emit('delete', this.index);
            this.show = false;
        },
        loadPopup(data) {
            this.popup = {
                ...data,
                media_type: data.video ? 'video' : 'image',
            };

            // Cover scenario where selected price was deleted.
            if (this.popup.content.price !== 'hide'
            && this.popup.content.price !== 'first'
            && ! this.pricingPlanExists(this.popup.content.price)) {
                this.popup.content.price = 'first';
            }

            this.originalVideoThumbnailUrl = data.video_image || null; // This is intended so it is not reactive and does not get updated when user selects new thumbnail. This is used to show thumbnail warning within video upload component when user selects new thumbnail but does not save the popup.
        },
        addImage(image) {
            this.popup.image = image.path;
            this.popup.image_url = {
                full: image.url
            };
        },
        removeImage() {
            this.popup.image = null;
            this.popup.image_url = null;
        },
        getEmptyPopup() {
            return {
                encoded_id: null,
                name: '',
                image: null,
                image_url: null,
                video: null,
                video_url: null,
                video_type: 'bunny',
                video_image: null,
                video_embed_code: null,
                video_image_url: null,
                media_type: 'image',
                content: {
                    headline: '',
                    body: '',
                    price: 'hide',
                    buttonLabel: this.trans('Get Access'),
                    buttonColor: this.defaultButtonColor,
                    buttonUrl: '',
                },
            };
        },
        addVideo(videoUrl, videoType) {
            this.popup.video_url = videoUrl;
            this.popup.video_type = videoType;
        },
        removeVideo() {
            this.popup.video_url = null;
            this.popup.video_type = null;
            this.popup.video_image = null;
            this.popup.video_embed_code = null;
        },
        /**
         * Add's custom uploaded thumbnail to popup's video_image property.
         *
         * @param   {String}  thumbnailUrl
         *
         * @return  {Void}
         */
        addVideoThumbnail(thumbnailUrl) {
            this.popup.video_image = thumbnailUrl;
        },

        /**
         * Check if pricing plan selected in the popup exists.
         *
         * This is being used to select the "first" plan if old plan was
         * deleted.
         *
         * @param   {String}  planEncodedId
         *
         * @return  {Boolean}                 True if the pricing plan exists, false otherwise.
         */
        pricingPlanExists(planEncodedId) {
            return this.pricingPlans.some(plan => plan.encoded_id === planEncodedId);
        },
    },
};
</script>
